import { useEffect } from "react";
import Papa from "papaparse";
import { fetchR2CsvText } from "../../utils/fetchR2Csv";

export function useIndexAndCrumbsLoader({
  areaType,
  minPopOnly,
  populationByCity,
  setPopulationByCity,
  setLevelItems,
  setSelectedName,
  setCrumbsByName,
  setRawCityNames,
  rawCityNames,
  levelItems,
  selectedName,
}) {
  // Load city population lookup
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    (async () => {
      try {
        // Prefer new filename, fall back to legacy
        let res = await fetch("/lookups/city_800_population.csv");
        if (!res.ok) {
          res = await fetch("/lookups/city_population.csv");
          if (!res.ok) return;
        }
        const text = await res.text();
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          worker: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            const map = {};
            for (const r of result.data || []) {
              const city =
                (r.City ?? r.city ?? r.CityName ?? "").toString().trim();
              const pop =
                Number(r.Population ?? r.population ?? r.Pop ?? r.Pop2020 ?? r.Pop2019);
              if (!city || !Number.isFinite(pop)) continue;
              map[city] = pop;
            }
            setPopulationByCity(map);
          },
        });
      } catch {
        /* file optional */
      }
    })();
  }, [setPopulationByCity]);

  // Load small files for City/Zipcode/Neighborhood (index + latest) for fast type-ahead
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    
    const small =
      areaType === "City" || areaType === "Zipcode" || areaType === "Neighborhood";
    if (!small) return;

    let cancelled = false;

    async function loadSmall() {
      // Reset current lists/maps for this level
      setLevelItems((p) => ({ ...p, [areaType]: [] }));
      // Don't clear latestByMetro - it's handled by useStandardizedDataLoader for all area types
      console.log(`[INDEX_LOADER] Skipping latestByMetro clear for ${areaType} - handled by useStandardizedDataLoader`);

      // 1) Names index (very small)
      if (areaType === "Zipcode") {
        // For zipcode, use the optimized index file for fast dropdown loading
        try {
          const res = await fetch("/lookups/zipcode_index_optimized.csv", { cache: "no-store" });
          if (res.ok) {
            const csv = await res.text();
            Papa.parse(csv, {
              header: true,
              complete: (result) => {
                if (cancelled) return;
                const zipcodes = (result.data || [])
                  .map((r) => r.Zipcode)
                  .filter(Boolean);
                setLevelItems((p) => ({ ...p, [areaType]: zipcodes }));
                console.log(`[INDEX_LOADER] Loaded ${zipcodes.length} zipcodes from optimized index`);
              },
            });
          }
        } catch {}
      } else if (areaType === "State") {
        // For state, use Merged_No_Data Standard for fast dropdown loading
        try {
          const key = `public/data/Merged_No_Data/Standard/state_5y_50k_standard_merged.csv`;
          const csv = await fetchR2CsvText(key);
            Papa.parse(csv, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: "greedy",
              transformHeader: (h) => String(h || "").trim(),
              complete: (result) => {
                if (cancelled) return;
                const states = (result.data || [])
                  .map((r) => r.State)
                  .filter(Boolean);
                setLevelItems((p) => ({ ...p, [areaType]: states }));
                console.log(`[INDEX_LOADER] Loaded ${states.length} states from Merged_No_Data`);
              },
            });
        } catch {}
      } else if (areaType === "City") {
        // For city, use Merged_No_Data Standard for fast dropdown loading (matching metro structure)
        try {
          const key = `public/data/Merged_No_Data/Standard/city_5y_50k_standard_merged.csv`;
          const csv = await fetchR2CsvText(key);
            Papa.parse(csv, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: "greedy",
              transformHeader: (h) => String(h || "").trim(),
              complete: (result) => {
                if (cancelled) return;
                const cities = (result.data || [])
                  .map((r) => r.City)
                  .filter(Boolean);
                setLevelItems((p) => ({ ...p, [areaType]: cities }));
                console.log(`[INDEX_LOADER] Loaded ${cities.length} cities from Merged_No_Data`);
              },
            });
        } catch {}
      } else {
        // Use Merged_No_Data Standard for fast dropdown loading for all other levels
        try {
          const key = `public/data/Merged_No_Data/Standard/${areaType.toLowerCase()}_5y_50k_standard_merged.csv`;
          const csv = await fetchR2CsvText(key);
            Papa.parse(csv, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: "greedy",
              transformHeader: (h) => String(h || "").trim(),
              complete: (result) => {
                if (cancelled) return;
                const names = (result.data || [])
                  .map((r) => r[areaType] || r.Metro || r.State || r.County || r.CountyName || r.Zipcode || r.Neighborhood)
                  .filter(Boolean);
                setLevelItems((p) => ({ ...p, [areaType]: names }));
                console.log(`[INDEX_LOADER] Loaded ${names.length} ${areaType.toLowerCase()}s from Merged_No_Data/Standard`);
              },
            });
        } catch {}
      }

      // 2) Latest snapshot (small) - DISABLED: Using merged file data instead
      // The merged file data is now loaded by useBestArtifactsLoader
      // This prevents conflicts and ensures single source of truth
      console.log(`[INDEX_LOADER] Skipping latest.csv loading for ${areaType} - using merged file data instead`);
    }

    loadSmall();
    return () => {
      cancelled = true;
    };
  }, [areaType, setLevelItems]);

  // Robust level-aware index loader (all levels except City, Zipcode, Neighborhood)
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    
    if (areaType === "City" || areaType === "Zipcode" || areaType === "Neighborhood") return;

    const level = areaType;
    const lower = level.toLowerCase();
    // Single, explicit source file for indexes (no secondary sources)
    const key = `public/data/Merged_No_Data/Standard/${lower}_5y_50k_standard_merged.csv`;

    const fetchFirst = async () => {
      try {
        console.log(`[INDEX_CRUMBS_LOADER] Loading ${level} index from R2: ${key}`);
        const csv = await fetchR2CsvText(key);

        Papa.parse(csv, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            const rows = (result.data || []).filter(
              (r) => r && Object.values(r).some((v) => v != null && String(v).trim() !== "")
            );
            if (!rows.length) {
              setSelectedName("");
              return;
            }

            const keys = Object.keys(rows[0] || {});
            const want = level.toLowerCase();
            let nameKey =
              keys.find((k) => k === level) ||
              keys.find((k) => k === "Name") ||
              keys.find((k) => k.toLowerCase() === want) ||
              keys.find((k) => k.toLowerCase().startsWith(want)) ||
              keys[0];

            if (!nameKey) nameKey = level;

            const names = [];
            const crumbs = {};

            for (const r of rows) {
              const raw = r?.[nameKey] ?? r?.Name ?? r?.[level];
              const name = raw != null ? String(raw).trim() : "";
              if (!name) continue;
              if (!names.includes(name)) names.push(name);

              const clean = (x) => (x != null ? String(x).trim() : "");
              const c = {
                State: clean(r?.State),
                StateName: clean(r?.StateName),
                Metro: clean(r?.Metro),
                County: clean(r?.County),
                CountyName: clean(r?.CountyName),
                City: level === "City" ? name : clean(r?.City),
                Zipcode: clean(r?.Zipcode),
                Neighborhood: clean(r?.Neighborhood),
                RegionID: clean(r?.RegionID) || clean(r?.regionId) || clean(r?.region_id),
              };
              // Capture any parent RegionIDs if present in file
              for (const k of Object.keys(r)) {
                if (!k) continue;
                const keyName = String(k).trim();
                if (/^.*RegionID$/i.test(keyName)) {
                  c[keyName] = clean(r[k]);
                }
              }
              crumbs[name] = c;
            }

            // Keep raw list for toggling the checkbox later
            if (level === "City") setRawCityNames(names.slice());

            // No additional filtering - data is already filtered in 50K_5y_screened_data
            let toSet = names;
            setLevelItems((prev) => ({ ...prev, [level]: toSet.sort() }));
            setCrumbsByName((prev) => ({ ...prev, ...crumbs }));
            if (!names.includes(selectedName)) setSelectedName("");
          },
        });

      } catch (e) {
        console.error(`[INDEX_CRUMBS_LOADER] Error loading ${level} index:`, e);
        // none loaded → keep any existing list (don't wipe); just clear selection
        setSelectedName("");
      }
    };
    fetchFirst();
  }, [areaType, minPopOnly, populationByCity, setLevelItems, setCrumbsByName, setSelectedName, setRawCityNames, selectedName]);

  // Crumbs loader for small levels: Zipcode and Neighborhood
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }

    if (!(areaType === "Zipcode" || areaType === "Neighborhood")) return;

    const level = areaType;
    const lower = level.toLowerCase();
    const url = `/data/Merged_No_Data/Standard/${lower}_5y_50k_merged.csv`;

    let cancelled = false;
    (async () => {
      try {
        const key = `public/data/Merged_No_Data/Standard/${lower}_5y_50k_merged.csv`;
        const csv = await fetchR2CsvText(key);
        Papa.parse(csv, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            if (cancelled) return;
            const rows = (result.data || []).filter(
              (r) => r && Object.values(r).some((v) => v != null && String(v).trim() !== "")
            );
            if (!rows.length) return;

            const clean = (x) => (x != null ? String(x).trim() : "");
            const padZip5 = (z) => {
              const s = clean(z).replace(/\D+/g, "");
              return s.padStart(5, "0").slice(0, 5);
            };
            const crumbs = {};

            for (const r of rows) {
              const key = level === "Zipcode" ? padZip5(r.Zipcode) : clean(r.Neighborhood);
              if (!key) continue;

              const c = {
                State: clean(r.State),
                StateName: clean(r.StateName),
                Metro: clean(r.Metro),
                County: clean(r.County),
                CountyName: clean(r.CountyName),
                City: clean(r.City),
                Zipcode: level === "Zipcode" ? key : clean(r.Zipcode),
                Neighborhood: clean(r.Neighborhood),
                RegionID: clean(r.RegionID) || clean(r.regionId) || clean(r.region_id),
              };
              // Capture any parent RegionIDs if present
              for (const k of Object.keys(r)) {
                if (!k) continue;
                const nk = String(k).trim();
                if (/^.*RegionID$/i.test(nk)) {
                  c[nk] = clean(r[k]);
                }
              }
              crumbs[key] = c;
            }

            if (Object.keys(crumbs).length) {
              setCrumbsByName((prev) => ({ ...prev, ...crumbs }));
            }
          },
        });
      } catch (e) {
        console.error(`[INDEX_CRUMBS_LOADER_SMALL] Error loading ${level} crumbs:`, e);
      }
    })();

    return () => { cancelled = true; };
  }, [areaType, setCrumbsByName]);

  // No-op for population filter (feature removed)

  // Fallback for City: populate names and crumbs using datasets
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    
    if (areaType !== "City") return;

    const loadCityNames = async () => {
      if (Array.isArray(levelItems?.City) && levelItems.City.length > 0) return; // already have names
      try {
        // Use Merged_No_Data for city names (matching metro structure)
        const key = `public/data/Merged_No_Data/city_5y_50k_merged.csv`;
        const csv = await fetchR2CsvText(key);
        Papa.parse(csv, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          complete: (result) => {
            const rows = (result.data || []).filter((r) => r && r.City);
            const names = Array.from(new Set(rows.map((r) => String(r.City).trim()))).filter(Boolean).sort();
            if (names.length) {
              setLevelItems((prev) => ({ ...prev, City: names }));
            }
          },
        });
      } catch {}
    };

    const backfillParentsFromZip = async () => {
      console.log("[INDEX_CRUMBS_LOADER] backfillParentsFromZip called for City");
      try {
        const key = `public/data/Merged_Last_Month_Data/Standard/zipcode_5y_50k_standard_merged.csv`;
        const text = await fetchR2CsvText(key);
        console.log("[INDEX_CRUMBS_LOADER] Zipcode CSV loaded, parsing...");
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            const rows = (result.data || []).filter((r) => r && (r.City || r.Zipcode));
            const cityCrumbs = {};
            const clean = (x) => (x != null ? String(x).trim() : "");

            for (const r of rows) {
              const city = clean(r.City);
              if (!city) continue;
              const state = clean(r.State);
              const stateName = clean(r.StateName);
              const metro = clean(r.Metro);
              const county = clean(r.County);
              const countyName = clean(r.CountyName);
              if (!cityCrumbs[city]) cityCrumbs[city] = { City: city };
              if (state && !cityCrumbs[city].State) cityCrumbs[city].State = state;
              if (stateName && !cityCrumbs[city].StateName) cityCrumbs[city].StateName = stateName;
              if (metro && !cityCrumbs[city].Metro) cityCrumbs[city].Metro = metro;
              if (county && !cityCrumbs[city].County) cityCrumbs[city].County = county;
              if (countyName && !cityCrumbs[city].CountyName) cityCrumbs[city].CountyName = countyName;
            }

            console.log("[INDEX_CRUMBS_LOADER] Parsed zipcode rows:", rows.length);
            console.log("[INDEX_CRUMBS_LOADER] CityCrumbs object size:", Object.keys(cityCrumbs).length);
            console.log("[INDEX_CRUMBS_LOADER] Sample cityCrumbs:", Object.keys(cityCrumbs).slice(0, 3).map(k => ({ key: k, metro: cityCrumbs[k].Metro, state: cityCrumbs[k].State })));
            
            if (Object.keys(cityCrumbs).length) {
              console.log(`[INDEX_CRUMBS_LOADER] Setting City crumbs with Metro field:`, Object.keys(cityCrumbs).slice(0, 3).map(k => ({ key: k, metro: cityCrumbs[k].Metro })));
              setCrumbsByName((prev) => ({ ...prev, ...cityCrumbs }));
            } else {
              console.log("[INDEX_CRUMBS_LOADER] CityCrumbs is empty, not setting");
            }
          },
        });
      } catch {}
    };

    loadCityNames();
    backfillParentsFromZip();
  }, [areaType, levelItems, setLevelItems, setCrumbsByName]);

  // Backfill crumb for the current selection (ensures Path shows for Metro/County/City/Zipcode)
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    
    if (!selectedName) return;
    if (!["Metro", "County", "City", "Zipcode"].includes(areaType)) return;

    const cur = (name) => name;
    (async () => {
      try {
        const key = `public/data/Merged_Last_Month_Data/Standard/zipcode_5y_50k_standard_merged.csv`;
        const text = await fetchR2CsvText(key);
        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            const rows = (result.data || []);
            const clean = (x) => (x != null ? String(x).trim() : "");
            let rec = null;

            if (areaType === "Metro") {
              rec = rows.find((r) => clean(r.Metro) === selectedName);
              if (rec) {
                setCrumbsByName((prev) => ({
                  ...prev,
                  [selectedName]: {
                    ...(prev[selectedName] || {}),
                    State: clean(rec.State),
                    StateName: clean(rec.StateName),
                    Metro: clean(rec.Metro),
                  },
                }));
              }
            } else if (areaType === "County") {
              rec = rows.find((r) => clean(r.County) === selectedName || clean(r.CountyName) === selectedName);
              if (rec) {
                setCrumbsByName((prev) => ({
                  ...prev,
                  [selectedName]: {
                    ...(prev[selectedName] || {}),
                    State: clean(rec.State),
                    StateName: clean(rec.StateName),
                    Metro: clean(rec.Metro),
                    County: selectedName,
                  },
                }));
              }
            } else if (areaType === "City") {
              rec = rows.find((r) => clean(r.City) === selectedName);
              if (rec) {
                setCrumbsByName((prev) => ({
                  ...prev,
                  [selectedName]: {
                    ...(prev[selectedName] || {}),
                    State: clean(rec.State),
                    StateName: clean(rec.StateName),
                    Metro: clean(rec.Metro),
                    County: clean(rec.County),
                    City: clean(rec.City),
                  },
                }));
              }
            } else if (areaType === "Zipcode") {
              rec = rows.find((r) => clean(r.Zipcode) === selectedName);
              if (rec) {
                setCrumbsByName((prev) => ({
                  ...prev,
                  [selectedName]: {
                    ...(prev[selectedName] || {}),
                    State: clean(rec.State),
                    StateName: clean(rec.StateName),
                    Metro: clean(rec.Metro),
                    County: clean(rec.County),
                    City: clean(rec.City),
                    Zipcode: clean(rec.Zipcode),
                  },
                }));
              }
            }
          },
        });
      } catch {
        // ignore
      }
    })();
  }, [areaType, setCrumbsByName, selectedName]);

  // Fallback parents & names for County/Metro/State from ZIP index
  useEffect(() => {
    // Skip if areaType is empty
    if (!areaType || areaType === "") {
      return;
    }
    
    const backfillFromZip = async () => {
      try {
        const key = `public/data/Merged_Last_Month_Data/Standard/zipcode_5y_50k_standard_merged.csv`;
        const text = await fetchR2CsvText(key);

        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            const rows = (result.data || []).filter(
              (r) => r && (r.Zipcode || r.City || r.CountyName || r.State || r.Metro)
            );
            if (!rows.length) return;

            const clean = (x) => (x != null ? String(x).trim() : "");
            const crumbs = {};
            const names = { County: new Set(), Metro: new Set(), State: new Set() };

            for (const r of rows) {
              const state = clean(r.State);
              const metro = clean(r.Metro);
              const county = clean(r.CountyName);

              if (county) {
                names.County.add(county);
                if (!crumbs[county]) crumbs[county] = { County: county };
                if (state && !crumbs[county].State) crumbs[county].State = state;
                if (metro && !crumbs[county].Metro) {
                  console.log(`[COUNTY_CRUMBS] Setting metro for county ${county}: ${metro}`);
                  crumbs[county].Metro = metro;
                }
              }
              if (metro) {
                names.Metro.add(metro);
                if (!crumbs[metro]) crumbs[metro] = {};
                if (state && !crumbs[metro].State) crumbs[metro].State = state;
              }
              if (state) {
                names.State.add(state);
              }
            }

            if (Object.keys(crumbs).length) {
              setCrumbsByName((prev) => ({ ...prev, ...crumbs }));
            }

            const needCounty = !Array.isArray(levelItems?.County) || levelItems.County.length === 0;
            const needMetro  = !Array.isArray(levelItems?.Metro)  || levelItems.Metro.length  === 0;
            const needState  = !Array.isArray(levelItems?.State)  || levelItems.State.length  === 0;

            const next = { ...levelItems };
            if (needCounty) next.County = Array.from(names.County).sort();
            if (needMetro)  next.Metro  = Array.from(names.Metro).sort();
            if (needState)  next.State  = Array.from(names.State).sort();

            if (needCounty || needMetro || needState) setLevelItems(next);
          },
        });
      } catch {
        /* ignore */
      }
    };

    backfillFromZip();
  }, [areaType, setCrumbsByName, setLevelItems, levelItems]);
}


